/**
 * useAnsweredDecisionSummary — WHAT the decision was answered with.
 *
 * emuw-v1-5 `shared-decision-components` Phase 3 task 10.
 *
 * IT REFETCHES. That is not an optimisation choice, it is the only thing that
 * works, and a hook that read what the host already holds would resolve `null`
 * forever — leaving both the refusal card and the answered-elsewhere card stuck
 * permanently on the un-naming copy, while passing every unit test written
 * against it. Verified on the tree:
 *
 *   * Ora's drawer has NO hitl-history in scope at all. `fetchHitlHistory` lives
 *     inside the separate history section's own local state.
 *   * The add-in DOES hold `hitlEvents`, but fetches them only while the status is
 *     `paused_hitl`/`warm_hitl` or terminal, keyed on the instance. On this exact
 *     transition the status leaves `paused_hitl`, the key does not change, and the
 *     already-fetched marker still matches — so the cached events are the ones
 *     fetched BEFORE the answer landed and can never contain it.
 *   * Neither refusal shape carries it: the 409 detail is a status-mismatch
 *     sentence and the warm `already_handled` ack has no detail field at all.
 *
 * SEPARATE from `useAnsweredElsewhere` because the two have different triggers — a
 * state transition versus a network read — and only this one can fail. When it
 * does, `answeredWith` stays `null` and the card falls back to copy that says
 * something true. A refusal that turns into an error toast because history was
 * briefly unreachable is worse than an unnamed answer.
 *
 * Fires AT MOST ONCE per resolved question key, and never while a question is
 * still on screen (`enabled` false ⇒ no request). No new call on any mount or
 * first-render path; one call, on a transition the AM already caused.
 *
 * The transport is injected because it differs per host — Ora's Remix-side GET,
 * the add-in's orchestrator client — not because the rule does. Importing either
 * host's client here would drag it into the other's bundle.
 */

import { useEffect, useRef, useState } from "react";

import type { HitlEvent } from "./components/WorkflowHitlHistorySection";
import { hitlQuestionKey } from "./hitlQuestionKey";
import { summarizeHitlResponse } from "./hitlSummary";

export interface UseAnsweredDecisionSummaryArgs {
  instanceId: string | null;
  /** The question that was on screen. Watched by its content key. */
  hitlQuestion: unknown;
  /** Fetch only once the decision actually resolved. */
  enabled: boolean;
  fetchHitlHistory: (instanceId: string) => Promise<HitlEvent[]>;
}

/**
 * WHICH GATE a question belongs to, as the question payload spells it.
 *
 * `step_id` first, because that is the field `HitlEvent` carries and therefore
 * the only one that can be compared without translation; `checkpoint_id`/`kind`
 * after it, because older payloads carry only those (same pair
 * `stepViewFor.readCheckpointId` reads). `null` when the payload names no gate
 * at all — a miss, never a guess.
 */
export function readGateId(hitlQuestion: unknown): string | null {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return null;
  const q = hitlQuestion as Record<string, unknown>;
  for (const key of ["step_id", "checkpoint_id", "kind"]) {
    const v = q[key];
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return null;
}

/** The bare gate id: a checkpoint reaches this surface fully qualified from the
 *  orchestration (`carrier_mortgagee_update.record_locked`) and BARE from the
 *  history table's `step_id` (`record_locked`), and the two are one gate. */
function bareGateId(id: string): string {
  const dot = id.lastIndexOf(".");
  return dot >= 0 ? id.slice(dot + 1) : id;
}

/** Same gate, tolerating the qualified/bare spelling split above. */
function sameGate(eventStepId: string | null, gateId: string): boolean {
  if (!eventStepId) return false;
  return (
    eventStepId === gateId || bareGateId(eventStepId) === bareGateId(gateId)
  );
}

/**
 * The highest-`sequence` answered event whose stored question matches the one
 * that cleared; failing that, the highest-`sequence` answered event FOR THE SAME
 * GATE.
 *
 * The fallback matters: a gate whose stored `question` differs from the live one
 * (re-emitted with fresh SAS tokens, say) would otherwise resolve to nothing and
 * silently drop back to the un-naming copy.
 *
 * BUT IT IS GATE-SCOPED, and that is the point. The fallback used to take the
 * highest-sequence answered event outright, with nothing tying it to the gate
 * the card is showing: a run whose G1 gate is immediately followed by G2 answers
 * BOTH before this hook's history fetch resolves, G1's stored question misses
 * the byte-match, and the card then names G2's answer as if it were G1's — this
 * repo's own "no checkpoint stamp, answer attributed to the wrong gate" bug,
 * on the read path.
 *
 * So, once this card HAS shown a question, the answer it names has to be that
 * question's own gate or nothing: no answered event for the gate (or a question
 * that named no gate at all) resolves to `null`, and the card falls back to copy
 * that is merely unspecific. That is the designed degradation (see the file
 * docstring) and is strictly better than confidently naming somebody else's
 * answer.
 *
 * THE ONE EXCEPTION is a card that never saw a question — it mounted already
 * answered, on the other surface, while the pane was closed. There is no gate it
 * could be contradicting, and "the most recent answer recorded on this run" is
 * both the best available reading and the behaviour that state has always had.
 */
export function selectAnsweredEvent(
  events: HitlEvent[],
  questionKey: string | null,
  gateId?: string | null,
): HitlEvent | null {
  const answered = events.filter((e) => e && e.answered_at != null);
  if (answered.length === 0) return null;
  const bySequenceDesc = [...answered].sort((a, b) => b.sequence - a.sequence);
  if (questionKey) {
    const matched = bySequenceDesc.find(
      (e) => e.question != null && hitlQuestionKey(e.question) === questionKey,
    );
    if (matched) return matched;
  }
  if (!gateId) {
    // Nothing was ever on screen here: no gate to misattribute to.
    return questionKey ? null : (bySequenceDesc[0] ?? null);
  }
  return bySequenceDesc.find((e) => sameGate(e.step_id, gateId)) ?? null;
}

export function useAnsweredDecisionSummary({
  instanceId,
  hitlQuestion,
  enabled,
  fetchHitlHistory,
}: UseAnsweredDecisionSummaryArgs): { answeredWith: string | null } {
  const [answeredWith, setAnsweredWith] = useState<string | null>(null);

  // The key of the question that was LAST on screen. Captured while it is still
  // there, because by the time `enabled` flips true the question is already gone.
  const lastKeyRef = useRef<string | null>(null);
  const currentKey = hitlQuestion != null ? hitlQuestionKey(hitlQuestion) : null;
  if (currentKey !== null) lastKeyRef.current = currentKey;

  // WHICH gate that question was, captured on the same frame and for the same
  // reason — it is what scopes the fallback below to this card's own gate.
  const lastGateIdRef = useRef<string | null>(null);
  if (hitlQuestion != null) {
    const gateId = readGateId(hitlQuestion);
    if (gateId !== null) lastGateIdRef.current = gateId;
  }

  // What we have already fetched for, so this fires at most once per resolution.
  const fetchedForRef = useRef<string | null>(null);

  // Held by ref so an unmemoized caller arrow does not retrigger the fetch.
  const fetchRef = useRef(fetchHitlHistory);
  fetchRef.current = fetchHitlHistory;

  // The instance the refs above belong to. `undefined` means "not seen yet".
  const seenInstanceRef = useRef<string | null | undefined>(undefined);

  // Start over when the host swaps to a DIFFERENT run — and only then. Running
  // this on mount too wiped the key and gate id the render above had just
  // captured, so a question that is on screen for a single render (the host
  // resolving it on the very next frame) reached the fetch with no identity at
  // all. Everything it sets is already at that value on mount, so skipping the
  // first run changes nothing except what survives it.
  useEffect(() => {
    if (seenInstanceRef.current === instanceId) return;
    const isFirstRun = seenInstanceRef.current === undefined;
    seenInstanceRef.current = instanceId;
    if (isFirstRun) return;
    setAnsweredWith(null);
    fetchedForRef.current = null;
    lastKeyRef.current = null;
    lastGateIdRef.current = null;
  }, [instanceId]);

  useEffect(() => {
    if (!enabled || !instanceId) return;
    const marker = `${instanceId}:${lastKeyRef.current ?? "unknown"}`;
    if (fetchedForRef.current === marker) return;
    fetchedForRef.current = marker;

    let cancelled = false;
    void (async () => {
      try {
        const events = await fetchRef.current(instanceId);
        if (cancelled) return;
        const event = selectAnsweredEvent(
          Array.isArray(events) ? events : [],
          lastKeyRef.current,
          lastGateIdRef.current,
        );
        if (!event) return;
        setAnsweredWith(
          summarizeHitlResponse(event.response, event.event_type) || null,
        );
      } catch {
        // Deliberately silent. `answeredWith` stays null and the card shows copy
        // that is still true; turning an unreachable history endpoint into a
        // visible error would be worse than an unnamed answer.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, instanceId]);

  return { answeredWith };
}

export default useAnsweredDecisionSummary;
